import { useTranslation } from "react-i18next"

import { LeaderboardTrend } from "@/components/leaderboard/leaderboard-trend"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { useInitials } from "@/hooks/use-initials"
import { formatXpNumber } from "@/lib/format-xp"
import { cn } from "@/lib/utils"
import type { LeaderboardRow } from "@/types/leaderboard"

/**
 * OKS-51 — Single ranking-list row.
 *
 * UX Spec §2.1:
 *   - rank number (left, fixed width, tabular nums)
 *   - avatar (32px) — falls back to initials when `avatar_url` is null
 *   - display name (truncate at row width, single line)
 *   - trend cell (OKS-394 — ▲/▼/—, fixed width, before XP)
 *   - XP value (right-aligned, tabular nums, locale-grouped)
 *
 * # `viewerHighlight`
 *
 * When the viewer's own row is INSIDE the visible top-50, the row
 * carries an accent border + bg + " (You)" suffix on the display name
 * (UX Spec §2.1 + §6). When the viewer is outside top-50, the
 * separate `<LeaderboardSelfPin>` renders the highlight instead — the
 * row itself uses the regular variant.
 *
 * # Accessibility (UX Spec §6)
 *
 * `aria-label` on the row reads "Rank {N}, {display_name}, {XP} XP"
 * so a screenreader announces position + identity + score in one
 * sweep. Avatar `<img>` carries `alt=""` (decorative; the name is in
 * the row's accessible label). The `(You)` suffix is text, not
 * styling — colour-blind viewers identify their own row by reading
 * the suffix.
 */
interface LeaderboardRowProps {
  row: LeaderboardRow
  /** When true, render the accent treatment + add the "(You)" suffix. */
  viewerHighlight?: boolean
  /** Optional test ID — defaults to `leaderboard-row-{rank}`. */
  testId?: string
}

export function LeaderboardListRow({
  row,
  viewerHighlight = false,
  testId,
}: LeaderboardRowProps) {
  const { t, i18n } = useTranslation()
  const initials = useInitials()
  const formattedXp = formatXpNumber(row.xp, i18n.language)
  const dataTestId = testId ?? `leaderboard-row-${row.rank}`
  const ariaLabel = t("leaderboard.row.aria_label", {
    rank: row.rank,
    name: row.display_name,
    xp: formattedXp,
  })

  return (
    <li
      data-testid={dataTestId}
      aria-label={ariaLabel}
      data-viewer={viewerHighlight ? "true" : undefined}
      className={cn(
        "flex items-center gap-3 rounded-md px-3 py-2 text-sm",
        // Alternating-row subtle bg lives on the parent <ul> (`even:bg-muted/30`).
        viewerHighlight && [
          "bg-accent/10 border-accent border",
          "ring-accent/40 ring-offset-1",
        ],
      )}
    >
      <span
        className="text-muted-foreground w-8 shrink-0 text-center font-medium tabular-nums"
        aria-hidden="true"
      >
        {row.rank}
      </span>
      <Avatar className="size-8 shrink-0">
        <AvatarFallback className="text-xs">
          {initials(row.display_name)}
        </AvatarFallback>
      </Avatar>
      <span className="min-w-0 flex-1 truncate" title={row.display_name}>
        {row.display_name}
        {viewerHighlight && (
          <span className="text-muted-foreground ml-1.5 text-xs font-medium">
            {t("leaderboard.row.you_suffix")}
          </span>
        )}
      </span>
      <LeaderboardTrend trend={row.trend} />
      <span className="shrink-0 text-right font-medium tabular-nums">
        {formattedXp}{" "}
        <span className="text-muted-foreground text-xs">
          {t("leaderboard.xp_suffix")}
        </span>
      </span>
    </li>
  )
}
